import type { VideoProvider } from '@wr/shared';
import { get } from '../../api.js';
import type { LocalStore } from '../../offline/store.js';

/** An item of the booth kiosk, as the device keeps it. */
export interface KioskItem {
  id: string;
  format: 'video' | 'pdf';
  kind: string;
  title: string;
  lang: string;
  provider: VideoProvider | null;
  video_id: string | null;
  video_hash: string | null;
  size_bytes: number | null;
  has_thumbnail: boolean;
  /** Round 2: what a device may keep for a show without network, and its version. */
  has_file?: boolean;
  has_offline?: boolean;
  offline_size_bytes?: number | null;
  offline_version?: string | null;
  file_version?: string | null;
}

const meta = (eventId: string) => `kiosk:${eventId}`;
export const thumbnailUrl = (eventId: string, id: string) =>
  `/api/kiosk/events/${eventId}/assets/${id}/thumbnail`;
export const fileUrl = (eventId: string, id: string) =>
  `/api/kiosk/events/${eventId}/assets/${id}/file`;

/**
 * The kiosk's content (brief sections 7.4, 7.5 and 9: the kiosk works
 * offline): the list from the portal when it answers, else the one the
 * device kept. Its files (offline copies of the videos, PDFs, thumbnails)
 * are downloaded only once the person confirms it with its size (round 2,
 * KioskPrepare), never by themselves.
 */
export async function loadKiosk(store: LocalStore | null, eventId: string): Promise<KioskItem[]> {
  try {
    const { items } = await get<{ items: KioskItem[] }>(
      `/kiosk/events/${encodeURIComponent(eventId)}`,
    );
    await store?.setMeta(meta(eventId), items).catch(() => undefined);
    return items;
  } catch (error) {
    const kept = await store?.getMeta<KioskItem[]>(meta(eventId)).catch(() => null);
    if (kept) return kept;
    throw error;
  }
}

/** A file kept before round 2 (the kiosk's own cache), else the network. */
export async function fileBytes(url: string): Promise<ArrayBuffer> {
  if (typeof caches !== 'undefined') {
    const kept = await caches.match(url).catch(() => undefined);
    if (kept) return kept.arrayBuffer();
  }
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(String(res.status));
  return res.arrayBuffer();
}

/** A thumbnail's address: the kept copy when there is one. */
export async function imageUrl(url: string): Promise<string> {
  if (typeof caches === 'undefined') return url;
  const kept = await caches.match(url).catch(() => undefined);
  return kept ? URL.createObjectURL(await kept.blob()) : url;
}
